import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectIsToastOpenFromState,
  selectToastMessageFromState,
} from 'src/app/Toast/redux/toast.selectors'
import { VerifiedSecondaryEmailToastMessage } from 'src/app/Toast/Toast'
import Box from 'src/components/Box/Box'
import EditProfileInfoForm, {
  EditProfileInfoFlatFields,
} from 'src/components/EditProfileInfoForm/EditProfileInfoForm'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import { updateProfilePhotoUrl } from 'src/navigation/urls'
import {
  getSecondaryEmails,
  PATCH_USER_PROFILE_INIT,
  PatchUserActions,
} from 'src/redux/actions/data/users.actions'
import { selectIsCspAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'
import { selectSecondaryEmailsFromState } from 'src/redux/selectors/data/secondaryEmails.selectors'
import { PatchUserRequest } from 'src/requests/patchUser.request'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface EditProfileInfoBoxProps {
  className?: string
  user: UserListModel
  patchingUser: boolean
  patchingUserProfile: boolean
  errorDetailMap?: ErrorDetailMap<string>
  patchUser: (
    body: Partial<PatchUserRequest>,
    patchType: PatchUserActions,
  ) => void
}

const EditProfileInfoBox: React.FC<
  React.PropsWithChildren<EditProfileInfoBoxProps>
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const secondaryEmails = useSelector(selectSecondaryEmailsFromState)
  const isToastOpen = useSelector(selectIsToastOpenFromState)
  const toastMessage = useSelector(selectToastMessageFromState)

  const verifiedEmails = secondaryEmails.filter((email) => email.emailVerified)

  const dispatch = useDispatch()

  const onSuccessfulSubmit = (flatFields: EditProfileInfoFlatFields): void => {
    props.patchUser(
      {
        firstName: flatFields.firstName,
        lastName: flatFields.lastName,
      },
      {
        type: PATCH_USER_PROFILE_INIT,
      },
    )
  }

  useEffect(() => {
    dispatch(getSecondaryEmails())
  }, [])

  // this is a bit hacky but it's preferable to managing the isEditing state through redux
  useEffect(() => {
    // stupid typescript casting nonsense
    const message = toastMessage as unknown as {
      type: () => React.ReactElement
    }

    // check if the toast is open and the message is the verified secondary email toast message
    if (
      isToastOpen &&
      message?.type()?.props.source ===
        (<VerifiedSecondaryEmailToastMessage />).type()?.props.source
    ) {
      setIsEditing(false)
    }
  }, [isToastOpen, toastMessage])

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        {translate('Name and Contact Info')}
        {!isEditing && (
          <HoverableLink
            onClick={(): void => {
              setIsEditing(true)
            }}
            ariaLabelSource={'Update'}
            css={css`
              display: flex;
              align-items: center;
            `}
            colorSvgOnHover
            disabled={isCspAuthenticated}
          >
            {translate('Update')}
            <SvgUpdate
              className={'svg-update'}
              css={css`
                transform: scale(0.5);
              `}
            />
          </HoverableLink>
        )}
      </span>
      <div
        css={css`
          margin-top: 30px;
          display: flex;
          position: relative;
          ${getBreakpointDown(breakpointSizes.MD)(css`
            flex-direction: column-reverse;
          `)}
        `}
      >
        <div
          css={css`
            flex-grow: 2;
            display: flex;
            flex-direction: column;
          `}
        >
          <EditProfileInfoForm
            user={props.user}
            patchingUser={props.patchingUser}
            patchingUserProfile={props.patchingUserProfile}
            errorDetailMap={props.errorDetailMap}
            onSuccessfulSubmit={onSuccessfulSubmit}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            secondaryEmails={verifiedEmails}
          />
        </div>
        <div
          css={css`
            margin-left: 40px;
            ${getBreakpointDown(breakpointSizes.MD)(css`
              margin-left: 0px;
              align-self: center;
              margin-bottom: 30px;
            `)}
          `}
        >
          <div
            css={css`
              position: relative;
            `}
          >
            <UserAvatar user={props.user} size={AvatarSizes.ExtraExtraHuge} />
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                bottom: 5px;
                right: 5px;
                height: 30px;
                width: 30px;
                border-radius: 10px;
                background-color: ${styleConstants.colors.gray_lightest};
                box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.15);
                transition: box-shadow ${styleConstants.transitionSpeed.medium}
                  ease-out;
                &:hover {
                  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
                }
                ${isCspAuthenticated &&
                css`
                  opacity: 1;
                  > * {
                    opacity: 0.5;
                  }
                `}
              `}
              to={updateProfilePhotoUrl()}
              ariaLabelSource={'Update Profile Photo'}
              disabled={isCspAuthenticated}
            >
              <SvgUpdate
                css={css`
                  transform: scale(calc(16 / 24));
                `}
              />
            </HoverableLink>
          </div>
        </div>
      </div>
    </Box>
  )
}

export default EditProfileInfoBox
